import React, { useState, useContext } from "react";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useNavigate } from "react-router";
import { useAuth } from "../../context/AuthContext";
import {
  TrendingUpIcon,
  TrendingDownIcon,
  Database,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { reboot_rds } from "../../api/AdminApi";
import { GlobalContext } from "../../App";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setToastOpen, setToastMessage, setToastSeverity } =
    useContext(GlobalContext);

  const [showRebootDialog, setShowRebootDialog] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [isRebooting, setIsRebooting] = useState(false);

  const handleRebootDatabase = async () => {
    if (confirmationText.toLowerCase() !== "confirm") {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        "Please type 'confirm' to proceed with the database reboot."
      );
      return;
    }

    setIsRebooting(true);
    try {
      await reboot_rds();
      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage(
        "Database reboot initiated successfully. Expected downtime: ~30 seconds."
      );
      setShowRebootDialog(false);
      setConfirmationText("");
    } catch (error) {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        "Failed to initiate database reboot: " +
          (error.message || "Unknown error")
      );
    } finally {
      setIsRebooting(false);
    }
  };

  const openRebootDialog = () => {
    setConfirmationText("");
    setShowRebootDialog(true);
  };

  const closeRebootDialog = () => {
    setShowRebootDialog(false);
    setConfirmationText("");
  };

  return (
    <div className="space-y-6">
      <div className="*:data-[slot=card]:shadow-xs grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 grid gap-4">
        {user.user_type === "admin" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Management
              </CardTitle>
              <CardDescription>
                Manage database operations and maintenance tasks
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="destructive"
                onClick={openRebootDialog}
                className="w-full"
              >
                <Database className="h-4 w-4 mr-2" />
                Reboot Database
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>

      {/* Reboot Confirmation Dialog */}
      {user.user_type === "admin" && (
        <Dialog open={showRebootDialog} onOpenChange={setShowRebootDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Confirm Database Reboot
              </DialogTitle>
              <DialogDescription className="space-y-2">
                <p>
                  <strong>Warning:</strong> This action will reboot the database
                  server.
                </p>
                <p>
                  Expected downtime: <strong>~30 seconds</strong>
                </p>
                <p>
                  All active connections will be temporarily interrupted during
                  this process.
                </p>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="confirmation">
                  Type <strong>"confirm"</strong> to proceed:
                </Label>
                <Input
                  id="confirmation"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type 'confirm' here"
                  className="w-full"
                />
              </div>
            </div>
            <DialogFooter className="sm:justify-start">
              <Button
                type="button"
                variant="destructive"
                onClick={handleRebootDatabase}
                disabled={
                  confirmationText.toLowerCase() !== "confirm" || isRebooting
                }
                className="w-full sm:w-auto"
              >
                {isRebooting ? "Rebooting..." : "Reboot Database"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeRebootDialog}
                disabled={isRebooting}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminDashboard;
